// Global Instructions Rule Applied!

import { useSelector } from 'react-redux'
import { RootState } from '../../../state_manager/store.ts'
import { EditorConfigSchema, type EditorConfig } from '../../../state_manager/data_models/editor_config_model.ts'

// Stable default so consumers always get a full EditorConfig object even
// before globalConfigs has loaded, or when the backend degraded editor_config
// to {} / the engine row is absent. Falls back to the Jodit engine.
const DEFAULT_EDITOR_CONFIG: EditorConfig = EditorConfigSchema.parse({})

// Read the cfg-driven editor config from globalConfigs. The value is already
// validated by EditorConfigSchema at fetch time (validateGlobalConfigRecord),
// so this is a plain, referentially-stable read.
export function useEditorConfig(): EditorConfig {
  return useSelector(
    (state: RootState) => state.globalConfigs?.editor_config ?? DEFAULT_EDITOR_CONFIG
  )
}

// Convenience: the resolved compose-body editor engine ('jodit' is the safe
// default until a surface is deliberately flipped to 'ckeditor').
export function useEditorEngine(): 'jodit' | 'ckeditor' {
  return useEditorConfig().engine === 'ckeditor' ? 'ckeditor' : 'jodit'
}

// Whether globalConfigs has loaded yet. Before it does, useEditorConfig falls
// back to the jodit DEFAULT, so a cold-loaded form would briefly render the
// Jodit editor (and a "Jodit" label) before flipping to CKEditor once the
// config arrives. Editor surfaces gate their render on this to avoid that
// flash. menu_items is empty only until the global-config payload lands — the
// same "loaded" signal useFundsData relies on (a logged-in user always has a
// populated menu, so this never hangs).
export function useEditorConfigReady(): boolean {
  return useSelector((state: RootState) => (state.globalConfigs?.menu_items?.length ?? 0) > 0)
}
